import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import classNames from 'classnames';
import styled from 'styled-components';

import { getShopRequestCommunityPropagationCandidates } from 'apis/ShopRequestAPI';
import Checkbox from 'components/lib/Checkbox';
import { CommunityType } from 'types/communities';

import { useShopRequestModalContext } from './ShopRequestModalContext';
import { SHOP_REQUEST_PROPAGATION_MODAL_MODE } from '../constants';
import ShopRequestPropagateModal from '../propagate/ShopRequestPropagateModal';
import { ShopRequestCreationObject } from '../types';

const Container = styled.div`
  display: flex;
  flex-flow: row;
  font-size: 12px;
`;

const Checkboxes = styled.div`
  display: flex;
  flex-flow: row;
  gap: 20px;
  margin-left: 10px;
`;

const StyledCheckbox = styled(Checkbox)`
  font-family: var(--font-regular);
  font-size: 12px;
  &&& .ant-checkbox + span {
    padding-inline-start: 10px;
    display: flex;
    align-items: center;
  }
`;

const CommunityCount = styled.div`
  display: flex;
  flex-direction: row;
  &.disabled {
    pointer-events: none;
    opacity: 0.25;
    .count {
    }
  }
  &:not(.disabled) {
    .count {
      color: var(--dark-blue);
      cursor: pointer;
    }
  }
`;

const OthersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

type Props = {
  shopRequest: ShopRequestCreationObject;
  community: CommunityType;
  isBaseCommunity?: boolean; // TODO: remove this once antd Form is used across CreateShopRequestModal
  disabled?: boolean;
};

const ShopRequestsModalPropagation = ({ shopRequest, community, isBaseCommunity = false, disabled = false }: Props) => {
  const {
    shopRequests,
    baseCommunityPropagateSelf,
    baseCommunityPropagateOthers,
    selectedBaseCommunity,
    setBaseCommunityPropagateSelf,
    setBaseCommunityPropagateOthers,
    setShopRequests,
  } = useShopRequestModalContext();

  const [showPropagationModal, setShowPropagationModal] = useState<boolean>(false);

  const { data: otherCommunities } = useQuery({
    queryKey: [
      'shopRequests',
      'propagation',
      'candidates',
      shopRequest.communityId,
      shopRequest.dueDate,
      selectedBaseCommunity?.id,
    ],
    queryFn: async () =>
      getShopRequestCommunityPropagationCandidates(
        shopRequest.communityId,
        shopRequest.dueDate,
        selectedBaseCommunity?.id
      ),
    enabled: Boolean(shopRequest?.communityId),
    refetchOnWindowFocus: false,
  });

  const toggleSelf = (event: CheckboxChangeEvent) => {
    if (isBaseCommunity) {
      setBaseCommunityPropagateSelf(!baseCommunityPropagateSelf);
    } else {
      const updatedShopRequests = [...shopRequests];
      updatedShopRequests[updatedShopRequests.indexOf(shopRequest)] = {
        ...shopRequest,
        propagation: {
          propagate_self: event.target.checked,
          propagate_others: shopRequest.propagation?.propagate_others || false,
        },
      };
      setShopRequests(updatedShopRequests);
    }
  };

  const toggleOthers = (event: CheckboxChangeEvent) => {
    if (isBaseCommunity) {
      setBaseCommunityPropagateOthers(!baseCommunityPropagateOthers);
    } else {
      const updatedShopRequests = [...shopRequests];
      updatedShopRequests[updatedShopRequests.indexOf(shopRequest)] = {
        ...shopRequest,
        propagation: {
          propagate_self: shopRequest.propagation?.propagate_self || false,
          propagate_others: event.target.checked,
        },
      };
      setShopRequests(updatedShopRequests);
    }
  };

  const propagateSelfChecked = isBaseCommunity ? baseCommunityPropagateSelf : shopRequest.propagation?.propagate_self;
  const propagateOthersChecked = isBaseCommunity
    ? baseCommunityPropagateOthers
    : shopRequest.propagation?.propagate_others;

  return (
    <Container>
      <div>Propagate to:</div>
      <Checkboxes>
        <StyledCheckbox checked={propagateSelfChecked} className={'green'} disabled={disabled} onChange={toggleSelf}>
          Self
        </StyledCheckbox>
        <OthersContainer>
          <StyledCheckbox
            checked={propagateOthersChecked}
            className={'green'}
            disabled={disabled}
            onChange={toggleOthers}
          >
            <span>Others</span>
          </StyledCheckbox>
          <CommunityCount className={classNames({ disabled: disabled })}>
            <span>(</span>
            <span className={'count'} onClick={() => setShowPropagationModal(true)}>
              {otherCommunities?.length}
            </span>
            <span>)</span>
          </CommunityCount>
        </OthersContainer>
      </Checkboxes>
      {showPropagationModal && (
        <ShopRequestPropagateModal
          mode={SHOP_REQUEST_PROPAGATION_MODAL_MODE.PREVIEW}
          subtitle={
            <span>
              Applies to the <span style={{ color: 'var(--dark-blue)' }}>{community?.comunity_name}</span> mystery shop
            </span>
          }
          baseCommunityId={selectedBaseCommunity?.id}
          communities={otherCommunities ?? []}
          open={showPropagationModal}
          onCancel={() => setShowPropagationModal(false)}
        />
      )}
    </Container>
  );
};

export default ShopRequestsModalPropagation;
